import { Image, View, ViewToken } from 'react-native';
import Images from '../../../../Images';
import React from 'react';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { useRerenderOnInterval } from '../../../../hooks/useRerenderOnInterval';
import IconButton from '../../../v2BaseComponents/IconButton';
import { getSpacing } from '../../../../utils/spacing';
import { PRFlatList } from '../../../shared/Scroll';

export type ScreenshotsCarouselOwnProps = {};

export type ScreenshotsCarouselProps = ScreenshotsCarouselOwnProps;

const usageScreenshotWidth = {
    mobile: 200,
    desktop: 200,
};

const usageScreenshotAspectRatio = 1290 / 2796;

const getUsageImageDimensions = (isMobile: boolean) => {
    const width = isMobile ? usageScreenshotWidth.mobile : usageScreenshotWidth.desktop;
    return { width, height: width / usageScreenshotAspectRatio };
};

const ScreenshotsCarousel: React.FC<ScreenshotsCarouselProps> = () => {
    const [maxCurrentIndex, setMaxCurrentIndex] = React.useState(0);
    const [minCurrentIndex, setMinCurrentIndex] = React.useState(0);
    const flatListRef = React.useRef<any>(null);
    useRerenderOnInterval({ interval: 250 }); // helps ensure the ref is set
    const isMobile = useIsMobileLayout();

    const renderScrollButton = (direction: 'prev' | 'next') => {
        if (minCurrentIndex === 0 && direction === 'prev') {
            return null;
        }

        if (maxCurrentIndex === Images.UsageScreenshots.length - 1 && direction === 'next') {
            return null;
        }

        return (
            <View
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: direction === 'prev' ? 0 : undefined,
                    right: direction === 'next' ? 0 : undefined,
                    zIndex: 1000,
                    borderRadius: 20,
                    ...({ boxShadow: '0px 3px 4.65px rgba(0, 0, 0, 0.27)' } as any),
                }}
            >
                <IconButton
                    icon={direction === 'prev' ? 'ChevronLeft2' : 'ChevronRight2'}
                    onPress={() => {
                        if (direction === 'prev') {
                            flatListRef.current.scrollToIndex({ index: minCurrentIndex - 1 });
                        } else {
                            flatListRef.current.scrollToIndex({ index: minCurrentIndex + 1 });
                        }
                    }}
                    style={{
                        borderRadius: 20,
                        borderColor: 'transparent',
                    }}
                    testID={`scroll-button-${direction}`}
                />
            </View>
        );
    };

    const onViewableItemsChanged = React.useCallback(({ viewableItems }: { viewableItems: ViewToken[] }) => {
        setMaxCurrentIndex(Math.max(...viewableItems.map((item) => item.index || 0)));
        setMinCurrentIndex(Math.min(...viewableItems.map((item) => item.index || 0)));
    }, []);

    return (
        <View style={{ position: 'relative', width: '100%' }}>
            {renderScrollButton('prev')}
            <PRFlatList
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={{
                    itemVisiblePercentThreshold: 90,
                }}
                ref={flatListRef}
                horizontal
                data={Images.UsageScreenshots}
                renderItem={({ item }) => (
                    <View style={{ padding: getSpacing(4) }}>
                        <Image
                            source={item}
                            defaultSource={item}
                            style={{
                                width: getUsageImageDimensions(isMobile).width,
                                height: getUsageImageDimensions(isMobile).height,
                            }}
                            resizeMethod="scale"
                        />
                    </View>
                )}
            />
            {renderScrollButton('next')}
        </View>
    );
};

export default React.memo(ScreenshotsCarousel);
